import React, { useState, useRef, useEffect, useCallback } from 'react';

type PlayStatus = 'playing' | 'paused' | 'stopped';

interface UsePlayMusicReturn {
  play: () => Promise<void>;
  pause: () => Promise<void>;
  reset: () => void;
  status: PlayStatus;
  audioRef: React.RefObject<HTMLAudioElement>;
  isPlaying: boolean;
  duration: number;
  error: Error | null;
}

export const usePlayMusic = (url: string): UsePlayMusicReturn => {
  const [status, setStatus] = useState<PlayStatus>('stopped');
  const [duration, setDuration] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !url) return;

    audio.src = url;
    audio.load();

    const handleEnded = () => {
      setStatus('stopped');
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleError = () => {
      setError(new Error('Failed to load audio'));
      setStatus('stopped');
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError);
      audio.pause();
    };
  }, [url]);

  const reset = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    setStatus('stopped');
    setError(null);
    audio.pause();
    audio.currentTime = 0;
    audio.src = url;
    audio.load();
  }, [url]);

  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      setError(null);
      await audio.play();
      setStatus('playing');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to play audio'));
      setStatus('stopped');
      throw err;
    }
  }, []);

  const pause = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      await audio.pause();
      setStatus('paused');
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to pause audio'));
      throw err;
    }
  }, []);

  return {
    play,
    pause,
    reset,
    status,
    audioRef,
    isPlaying: status === 'playing',
    duration,
    error,
  };
};
